import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { formatDueDate } from '../../../utils/taskUtils';
import DatePickerCalendar from './DatePickerCalendar/DatePickerCalendar';
import './TaskDueDate.css';

function TaskDueDate({
  task,
  hasSubtasks,
  isComplete,
  editingDueDateTaskId,
  isHovered,
  dateInputCallbackRef,
  onSetEditingDueDateTaskId,
  onUpdateTaskDueDate,
  hideEmptyDueDatePlaceholder = false,
  showIconOnlyWhenEmpty = false
}) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0, bottom: undefined });
  const [menuOpenAbove, setMenuOpenAbove] = useState(false);
  const menuRef = useRef(null);
  const containerRef = useRef(null);

  // Helper function to format date as YYYY-MM-DD
  const formatDateForInput = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper function to parse date string (YYYY-MM-DD or ISO string) to Date object in local timezone
  const parseDateString = (dateString) => {
    if (!dateString) return null;
    const dateStr = dateString.split('T')[0];
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  // Get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return formatDateForInput(today);
  };

  // Get tomorrow's date in YYYY-MM-DD format
  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    return formatDateForInput(tomorrow);
  };

  // Get a random weekday date 1-2 weeks in the future in YYYY-MM-DD format
  const getLaterDate = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Random number of days between 7 and 14 (1-2 weeks)
    const randomDays = Math.floor(Math.random() * 8) + 7; // 7-14 days
    
    // Start from randomDays in the future
    const laterDate = new Date(today);
    laterDate.setDate(today.getDate() + randomDays);
    
    // Ensure it's a weekday (Monday = 1, Friday = 5)
    // getDay() returns: 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    let dayOfWeek = laterDate.getDay();
    
    // If it's a weekend, move to the next Monday
    if (dayOfWeek === 0) {
      // Sunday: move forward 1 day to Monday
      laterDate.setDate(laterDate.getDate() + 1);
    } else if (dayOfWeek === 6) {
      // Saturday: move forward 2 days to Monday
      laterDate.setDate(laterDate.getDate() + 2);
    }
    
    return formatDateForInput(laterDate);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isMenuOpen &&
        containerRef.current &&
        menuRef.current &&
        !containerRef.current.contains(event.target) &&
        !menuRef.current.contains(event.target)
      ) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isMenuOpen]);

  // Handle Escape key to close menu
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isMenuOpen) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => {
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [isMenuOpen]);

  // Close menu on scroll or resize
  useEffect(() => {
    if (isMenuOpen) {
      const handleScrollOrResize = () => {
        setIsMenuOpen(false);
      };

      window.addEventListener('scroll', handleScrollOrResize, true);
      window.addEventListener('resize', handleScrollOrResize);
      return () => {
        window.removeEventListener('scroll', handleScrollOrResize, true);
        window.removeEventListener('resize', handleScrollOrResize);
      };
    }
  }, [isMenuOpen]);

  const handleSetToday = () => {
    onUpdateTaskDueDate(task.id, getTodayDate());
    setIsMenuOpen(false);
  };

  const handleSetTomorrow = () => {
    onUpdateTaskDueDate(task.id, getTomorrowDate());
    setIsMenuOpen(false);
  };

  const handleSetLater = () => {
    onUpdateTaskDueDate(task.id, getLaterDate(), true); // Pass true for is_later_date
    setIsMenuOpen(false);
  };

  const handleNoDueDate = () => {
    onUpdateTaskDueDate(task.id, null);
    setIsMenuOpen(false);
  };

  const handleDateSelect = (date) => {
    if (date) {
      const formattedDate = formatDateForInput(date);
      onUpdateTaskDueDate(task.id, formattedDate);
      setIsMenuOpen(false);
    }
  };

  // Estimated height of the calendar popover (used only to decide open above vs below)
  const CALENDAR_MENU_ESTIMATED_HEIGHT = 320;
  const MENU_GAP = 4;

  const handleDueDateClick = (e) => {
    e.stopPropagation();
    const willBeOpen = !isMenuOpen;
    setIsMenuOpen(willBeOpen);
    
    if (willBeOpen && containerRef.current) {
      // Calculate position for fixed menu (viewport coordinates)
      const rect = containerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom - MENU_GAP;
      const openBelow = spaceBelow >= CALENDAR_MENU_ESTIMATED_HEIGHT;
      const right = window.innerWidth - rect.right;
      if (openBelow) {
        setMenuPosition({
          top: rect.bottom + MENU_GAP,
          right,
          bottom: undefined
        });
        setMenuOpenAbove(false);
      } else {
        // Position above: anchor bottom of menu directly above the trigger (no height estimate)
        setMenuPosition({
          top: undefined,
          right,
          bottom: window.innerHeight - rect.top + MENU_GAP
        });
        setMenuOpenAbove(true);
      }
    }
  };
  const getClassName = () => {
    let className = 'task-due-date';
    
    if (!task.due_date) {
      className += ' task-due-date-empty';
    }
    
    const dateToCheck = (task.due_date && !isComplete) ? task.due_date : null;
    if (dateToCheck) {
      // Use task.is_later_date if checking the parent task's date, otherwise false
      const isLater = (dateToCheck === task.due_date) ? (task.is_later_date === true) : false;
      const formatted = formatDueDate(dateToCheck, isLater);
      if (formatted) {
        if (formatted.isToday) {
          className += ' task-due-date-today';
          if (isComplete) className += ' task-due-date-complete';
        } else if (formatted.isTomorrow) {
          className += ' task-due-date-tomorrow';
          if (isComplete) className += ' task-due-date-complete';
        } else if (!isComplete && formatted.isPast) className += ' task-due-date-past';
      }
    }
    
    return className;
  };

  const getTitle = () => {
    if (!task.due_date) return 'Due date';
    const dateStr = task.due_date.split('T')[0];
    const [year, month, day] = dateStr.split('-');
    const localeDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day)).toLocaleDateString();
    const isLater = task.is_later_date === true;
    const formatted = formatDueDate(task.due_date, isLater);
    if (!isComplete && formatted?.isPast) return `Was due ${localeDate}`;
    return localeDate;
  };

  const dayNameMap = {
    'Sunday': 'Sun',
    'Monday': 'Mon',
    'Tuesday': 'Tue',
    'Wednesday': 'Wed',
    'Thursday': 'Thu',
    'Friday': 'Fri',
    'Saturday': 'Sat'
  };

  const shortenDateText = (text, dayNamesOnly = false) => {
    if (!text) return text;
    
    // Shorten day names: "Thursday" -> "Thu", "Monday" -> "Mon", etc.
    if (dayNameMap[text]) {
      return dayNameMap[text];
    }
    
    if (dayNamesOnly) return text;
    
    // Shorten special cases (mobile)
    if (text === 'Today') return 'Td';
    if (text === 'Tomorrow') return 'Tm';
    
    // Shorten month names: "Jan 16" -> "J 16", etc.
    const monthNameMap = {
      'Jan': 'J',
      'Feb': 'F',
      'Mar': 'M',
      'Apr': 'A',
      'May': 'M',
      'Jun': 'J',
      'Jul': 'J',
      'Aug': 'A',
      'Sep': 'S',
      'Oct': 'O',
      'Nov': 'N',
      'Dec': 'D'
    };
    
    // Check if text matches pattern like "Jan 16" or "Feb 1"
    const monthDateMatch = text.match(/^([A-Za-z]{3})\s+(\d+)$/);
    if (monthDateMatch) {
      const month = monthDateMatch[1];
      const day = monthDateMatch[2];
      if (monthNameMap[month]) {
        return `${monthNameMap[month]} ${day}`;
      }
    }
    
    // For multiple dates (comma-separated), shorten each
    if (text.includes(', ')) {
      return text.split(', ').map(date => shortenDateText(date, dayNamesOnly)).join(', ');
    }
    
    // Return as-is for other formats
    return text;
  };

  const getDisplayText = () => {
    if (task.due_date) {
      const isLater = task.is_later_date === true;
      const formatted = formatDueDate(task.due_date, isLater);
      if (formatted) {
        if (!isComplete && formatted.isPast) return 'Overdue';
        return formatted.text;
      }
      return '';
    }
    if (hideEmptyDueDatePlaceholder) return '';
    if (showIconOnlyWhenEmpty) return '';
    return isHovered ? 'Due date' : '';
  };

  const getOverdueDateText = () => {
    if (!task.due_date || isComplete) return null;
    const formatted = formatDueDate(task.due_date, task.is_later_date === true);
    return formatted?.isPast ? formatted.text : null;
  };

  const getMobileDisplayParts = () => {
    const text = getDisplayText();
    if (!text || text === 'Due date') {
      return { text };
    }

    /* On mobile, show "!" for overdue (red style already on parent) */
    if (text === 'Overdue') {
      return { text: '!' };
    }

    /* On mobile, show "..." instead of "Later" for the due date */
    if (text === 'Later') {
      return { text: '...' };
    }

    const monthDateMatch = text.match(/^([A-Za-z]{3})\s+(\d+)$/);
    if (monthDateMatch) {
      return {
        month: monthDateMatch[1],
        day: monthDateMatch[2]
      };
    }

    return { text: shortenDateText(text) };
  };

  const hasDueDate = () => !!task.due_date;

  const menuOpenClassName = isMenuOpen ? 'task-due-date-menu-open' : '';
  
  const menuContent = isMenuOpen ? (
    <div 
      ref={menuRef}
      className="task-due-date-menu"
      style={{
        ...(menuPosition.top != null && { top: `${menuPosition.top}px` }),
        ...(menuPosition.bottom != null && { bottom: `${menuPosition.bottom}px` }),
        right: `${menuPosition.right}px`,
        zIndex: 10000
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <DatePickerCalendar
        selectedDate={task.due_date}
        openAbove={menuOpenAbove}
        onDateSelect={handleDateSelect}
        onTodayClick={handleSetToday}
        onTomorrowClick={handleSetTomorrow}
        onLaterClick={handleSetLater}
        onNoDueDateClick={handleNoDueDate}
      />
    </div>
  ) : null;
  
  const mobileDisplay = getMobileDisplayParts();

  return (
    <>
      <span 
        ref={containerRef}
        className={`${getClassName()} ${menuOpenClassName}${showIconOnlyWhenEmpty ? ' task-due-date-show-icon-only' : ''}`}
        onClick={handleDueDateClick}
        title={getTitle()}
      >
        <span className="task-due-date-text task-due-date-text-desktop">
          {getOverdueDateText() != null ? (
            <>
              <span className="task-due-date-overdue-label">Overdue</span>
              <span className="task-due-date-overdue-date">{shortenDateText(getOverdueDateText(), true)}</span>
            </>
          ) : (
            shortenDateText(getDisplayText(), true)
          )}
        </span>
        <span className="task-due-date-text task-due-date-text-mobile">
          {mobileDisplay.month ? (
            <span className="task-due-date-mobile-stack">
              <span className="task-due-date-month">{mobileDisplay.month}</span>
              <span className="task-due-date-day">{mobileDisplay.day}</span>
            </span>
          ) : (
            mobileDisplay.text
          )}
        </span>
        {!hasDueDate() && !hideEmptyDueDatePlaceholder && (
          <span className="task-due-date-calendar-icon">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="3" y="4" width="10" height="9" rx="1" stroke="currentColor" strokeWidth="1.2" fill="none"/>
              <path d="M3 6 L13 6" stroke="currentColor" strokeWidth="1.2"/>
              <path d="M6 2 L6 5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
              <path d="M10 2 L10 5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
            </svg>
          </span>
        )}
        {hasSubtasks && task.due_date && (
          <span 
            className="task-due-date-inherit-icon"
            title="Subtasks created will inherit this due date"
            onClick={(e) => e.stopPropagation()}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g transform="rotate(180 6 6)">
                <path d="M6 2L9 6H3L6 2Z" fill="currentColor"/>
                <path d="M6 6V10" stroke="currentColor" strokeWidth="1" strokeLinecap="round"/>
              </g>
            </svg>
          </span>
        )}
      </span>
      {menuContent && createPortal(menuContent, document.body)}
    </>
  );
}

export default TaskDueDate;

